import { buildSlice, CommonState, LoadingState } from '~libs/reduxUtils';
import { Appointment, Worker } from '~weekly-planner/lib/common';

import { simulate } from '~weekly-planner/actions/simulate';
import { sendSMS, typePrefix, usersApi } from '~weekly-planner/actions/users';

interface LoadingRows {
  [id: number]: LoadingState;
}

interface State extends CommonState<Worker> {
  loadingRows: LoadingRows;
  params: { [key: string]: any };
  simulated: Worker[];
  filterOptions: { [key: string]: any };
  orderBy: {
    availability: false;
    distance: false;
  };
}

const initialState: State = {
  loading: 'idle',
  loadingRows: {},
  hasLoaded: false,
  error: null,
  params: {},
  rows: [],
  simulated: [],
  filterOptions: {
    is_qualified: null,
    is_available: null,
    is_preferred: null,
    is_historical: null,
  },
  orderBy: {
    availability: false,
    distance: false,
  },
};

const slice = buildSlice<State, Worker>(typePrefix, initialState, {
  rtkQueryBridges: [
    {
      endpoint: usersApi.endpoints.getUsers,
      mode: 'list',
      selectRows: (payload: Worker[]) => payload,
    },
  ],
  customActions: {
    clearSimulated(state) {
      state.simulated = [];
      state.loadingRows = {};
    },
    filterUsers(state, action) {
      const { payload } = action;
      if ('key' in payload && 'value' in payload) {
        state.filterOptions[payload.key] = payload.value;
      } else {
        Object.entries(payload).forEach(([key, value]) => {
          state.filterOptions[key] = value;
        });
      }
    },
    orderUsers(state, action) {
      const { payload } = action;
      state.orderBy = payload;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(simulate.pending, (state, { meta: { arg } }) => {
      const { type, data, workers } = arg;
      switch (type) {
        case 'refresh': {
          workers.forEach((worker: Worker) => {
            state.loadingRows[parseInt(worker.id)] = 'pending';
          });
          break;
        }
        case 'assign': {
          data.forEach((appointment: Appointment) => {
            let { user_id } = appointment;
            if (user_id) {
              if (typeof user_id === 'string') user_id = parseInt(user_id);
              state.loadingRows[user_id] = 'pending';
            }
          });
          break;
        }

        case 'move':
        default: {
          const { origin, target } = workers;
          if (origin) state.loadingRows[parseInt(origin.id)] = 'pending';
          if (target) state.loadingRows[parseInt(target.id)] = 'pending';

          break;
        }
      }
    });
    builder.addCase(simulate.fulfilled, (state, action) => {
      const updatedWorkers = action.payload.data;
      updatedWorkers.forEach((worker: any) => {
        state.loadingRows[worker.id] = 'fulfilled';
        const index = state.simulated.findIndex((w: { id: any }) => w.id === worker.id);
        if (index > -1) {
          state.simulated[index] = worker;
        } else {
          state.simulated.push(worker);
        }
      });

      state.loading = 'fulfilled';
    });
    builder.addCase(sendSMS.fulfilled, (state) => {
      state.loading = 'fulfilled';
      //clear the error after sending SMS
      state.error = null;
    });
  },
});

export const { clear, clearSimulated, updateParams, filterUsers, orderUsers, clearError } = slice.actions;
export default slice.reducer;
